// locale.js — locale-aware date formatting that follows the app's selected language
// (i18nState.locale: th/en/zh/ja/ko), NOT the device locale.
//
// Why Intl.DateTimeFormat: passing the right BCP-47 locale makes the browser render
// the month names, weekday names, digit system, and even the calendar era for us —
// e.g. 'th-TH' yields the Thai Buddhist year (2569) and Thai month names, 'ja-JP'
// yields '2026年7月15日', 'ko-KR' '2026년 7월 15일', etc. So the fix everywhere is simply
// to feed the app locale instead of a hardcoded 'th-TH'.
//
// Reactivity: intlLocale() reads i18nState.locale (a reactive object). Any template
// expression or computed that calls these helpers therefore re-renders when the user
// switches language — exactly like $t. (See the note in InsightView.vue.)

import { i18nState } from '../i18n';

// app locale code -> BCP-47 tag for Intl. Thai keeps th-TH so the Buddhist era + Thai
// month/day names come through automatically.
const INTL_LOCALE = {
  th: 'th-TH',
  en: 'en-GB',
  zh: 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
};

// current Intl locale tag (reactive read of i18nState.locale)
export function intlLocale() {
  return INTL_LOCALE[i18nState.locale] || 'en-GB';
}

// normalise the many shapes callers pass (Date, 'YYYY-M-D', 'YYYY-MM-DD', ISO, moment)
// into a real Date, or null when unparseable/empty.
// Exported because new Date('YYYY-MM-DD') parses as UTC midnight — on a machine west of
// UTC that Date renders as the PREVIOUS local day, so weekday lookups must go through here.
export function toDate(v) {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate(); // moment
  const s = String(v);
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

// generic: format a date with arbitrary Intl options in the app locale
export function formatDateIntl(v, opts) {
  const d = toDate(v);
  if (!d) return '';
  return new Intl.DateTimeFormat(intlLocale(), opts).format(d);
}

// "วันพุธที่ 15 กรกฎาคม 2569" / "Wednesday, 15 July 2026" / "2026年7月15日水曜日"
export function formatFullDate(v) {
  return formatDateIntl(v, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

// "กรกฎาคม 2569" / "July 2026" / "2026年7月" / "2026년 7월"
export function formatMonthYear(v) {
  return formatDateIntl(v, { year: 'numeric', month: 'long' });
}

// "15 ก.ค. 2569" / "15 Jul 2026" / "2026年7月15日" — compact date for fields/labels
export function formatShortDate(v) {
  return formatDateIntl(v, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Monday-first SHORT weekday names for the current locale (calendar column headers).
// 2024-01-01 is a Monday; noon avoids any midnight/DST edge shifting the day.
//
// Thai override: Intl 'short' returns the FULL Thai weekday names ("จันทร์"…"อาทิตย์")
// in some browsers (Chrome), which overflow the calendar's 7-column grid and look cramped
// (esp. the last column). Use the conventional single-glyph Thai abbreviations instead.
const THAI_WEEKDAY_SHORT = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา']; // Mon → Sun
export function weekdayShortNames() {
  if (i18nState.locale === 'th') return THAI_WEEKDAY_SHORT.slice();
  const fmt = new Intl.DateTimeFormat(intlLocale(), { weekday: 'short' });
  const out = [];
  for (let i = 0; i < 7; i++) out.push(fmt.format(new Date(2024, 0, 1 + i, 12)));
  return out;
}

// Monday-first FULL weekday names for the current locale (day dropdowns).
export function weekdayLongNames() {
  const fmt = new Intl.DateTimeFormat(intlLocale(), { weekday: 'long' });
  const out = [];
  for (let i = 0; i < 7; i++) out.push(fmt.format(new Date(2024, 0, 1 + i, 12)));
  return out;
}

// SHORT month names [Jan..Dec] for the current locale (calendar month picker).
export function monthShortNames() {
  const fmt = new Intl.DateTimeFormat(intlLocale(), { month: 'short' });
  const out = [];
  for (let i = 0; i < 12; i++) out.push(fmt.format(new Date(2024, i, 15)));
  return out;
}

// year as shown for the current locale: Thai = Buddhist era (+543), everyone else Gregorian.
// Used by the calendar's year navigation where a bare number (not a full Intl date) is wanted.
export function displayYear(gregorianYear) {
  return i18nState.locale === 'th' ? gregorianYear + 543 : gregorianYear;
}

export default {
  intlLocale,
  toDate,
  formatDateIntl,
  formatFullDate,
  formatMonthYear,
  formatShortDate,
  weekdayShortNames,
  weekdayLongNames,
  monthShortNames,
  displayYear,
};
